import axios from "axios";
import { toast } from "react-hot-toast";
import { showLoading, hideLoading } from "../utils/loadingOverlay";

const apiAuth = axios.create({
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Function to handle logout
const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_token");

    // Redirect to login page
    window.location.href = "/login"; // Adjust the path as needed

    // Optional: Show logout message
    toast.error("Session expired. Please log in again.");
};

apiAuth.interceptors.request.use(
    (config) => {
      showLoading(); // 🔥 Show overlay before request
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      hideLoading();
      return Promise.reject(error);
    }
);

apiAuth.interceptors.response.use(
    (response) => {
        hideLoading(); // 🔥 Hide overlay on success
        if (response.config.method !== "get") {
            const msg = getSuccessMessage(response);
            msg && toast.success(msg);
        }
        return response;
    },
    (error) => {
        hideLoading(); // 🔥 Hide overlay on error
        let message = "Something went wrong. Please try again.";

        if (error.response) {
            // ✅ Server responded with a status code outside 2xx
            const { status, data } = error.response;

            // 🔥 Auto logout on 401 Unauthorized
            if (status === 401) {
                handleLogout();
                return Promise.reject(error); // Prevent further processing
            }

            message =
                data?.detail ||
                data?.message ||
                data?.error ||
                data?.msg ||
                getStatusMessage(status) ||
                message;
        } else if (error.request) {
            // ✅ Request was made but no response received
            if (error.message && error.message.includes("Network Error")) {
                message = "CORS error: Request blocked. Check server CORS settings.";
            } else if (!navigator.onLine) {
                message = "No internet connection. Please check your network.";
            } else {
                message = "No response from server. It might be down.";
            }
        } else {
            // ✅ Something else happened while setting up the request
            message = error.message || message;
        }

        // Opt-out for calls whose failure is expected and handled by the caller
        // (e.g. optional Google Calendar data): pass { silent: true } in the config.
        if (!error.config?.silent) toast.error(message);

        console.error("API Error:", {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
            details: error.message,
        });

        return Promise.reject(error);
    }
);

function getStatusMessage(status) {
  switch (status) {
    case 400:
      return "Bad request. Please check your input.";
    case 401:
      return "Keeping you safe! Please log back in.";
    case 403:
      return "Forbidden. You don’t have permission.";
    case 404:
      return "Not found. The requested resource doesn’t exist.";
    case 500:
      return "Server error. Please try again later.";
    case 503:
      return "Service unavailable. Try again soon.";
    default:
      return `Unexpected error (status: ${status})`;
  }
}

function getSuccessMessage(response) {
  return (
      response?.data?.message
  );
}

export default apiAuth;